/*eslint max-lines-per-function: ["error", 610]*/
/* eslint-disable complexity */
import React, { useState } from 'react';
import { Accordion, Badge, Button, Form, FormGroup } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import Select from 'react-select';

import FormRow from '../../../app/components/FormRow';
import {
  GetArticleDto,
  GetCardDto,
  GetExamDto,
  GetJudgmentWithSignedInfoDto,
  GetJudgmentWithSignedInfoDtoStatusP1Enum,
  GetPdfTemplateDtoTypeEnum,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import {
  ArticleMode,
  CardType,
  DrivingLicenseCategory,
  OccupationalMedicineCategory,
} from '../../../common/spdCore/validation/schemas';
import {
  SimP1StatusEnum,
  SimP1StatusEnumText,
} from '../../../common/spdCore/validation/enums';
import momencik from '../../../common/momencik';
import { ChooseSigned } from '../Users/ChooseSigned';
import { Role } from '../../../common/spdCore/authorization/roles';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { FormControlTextarea3 } from '../../../app/components/FormControlTextarea';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import CardSelector from '../Cards/CardSelector';
import { CardsLink } from '../Cards/CardsLink';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';

import { SaveAndPrintJudgment } from './SaveAndPrintJudgment';

interface JudgmentEditorProps {
  exam: GetExamDto;
  judg: Partial<GetJudgmentWithSignedInfoDto>;
  articles: GetArticleDto[];
  roles: Role[];
  handleOnClick: () => void;
  idx: string;
  mode: ArticleMode;
  cards: GetCardDto[];
}

const P1_STATUS_BG: Record<
  GetJudgmentWithSignedInfoDtoStatusP1Enum,
  string | null
> = {
  [GetJudgmentWithSignedInfoDtoStatusP1Enum.ToSign]: null,
  [GetJudgmentWithSignedInfoDtoStatusP1Enum.WaitingForP1]: 'secondary',
  [GetJudgmentWithSignedInfoDtoStatusP1Enum.AcceptedInP1]: 'success',
  [GetJudgmentWithSignedInfoDtoStatusP1Enum.RejectedInP1]: 'danger',
  [GetJudgmentWithSignedInfoDtoStatusP1Enum.ValidationFailed]: 'warning',
};

export const JudgmentEditor: React.FC<JudgmentEditorProps> = props => {
  const [judgment, setJudgment] = useState<
    Partial<GetJudgmentWithSignedInfoDto>
  >(props.judg);
  const [savedWithoutSign, setSavedWithoutSign] = useState(
    !!props.judg.withoutSign,
  );
  const [savingInProgress, setSavingInProgress] = useState(false);
  const [message, setMessage] = useState<string>('');

  const spd = useSpdCore();

  const recalculateNextExamDate = (
    articleId: string | undefined,
    judgmentDate: Date | null | undefined,
    contactWithFood: boolean,
  ) => {
    if (!props.exam.id || !judgmentDate) {
      return;
    }
    if (!articleId && !props.mode) {
      return;
    }
    spd.judgments
      .getNextExamDate(props.exam.id, {
        articleId,
        articleMode: articleId ? undefined : props.mode,
        contactWithFood,
        referenceDate: new Date(judgmentDate),
      })
      .then(nextExamDate => {
        setJudgment(prev => ({ ...prev, nextExamDate: nextExamDate ?? null }));
      })
      .catch(console.error);
  };

  const getAllArticles = (type?: CardType) => {
    const arr = props.articles
      .filter(o => (type ? (o.cardType as unknown as CardType) === type : true))
      .map(article => {
        return {
          value: article.id,
          label: article.description,
        };
      });
    return arr;
  };

  const getAllMedOccup = () => {
    const arr = Object.keys(OccupationalMedicineCategory).map(name => {
      return {
        value: name,
        label:
          OccupationalMedicineCategory[
            name as keyof typeof OccupationalMedicineCategory
          ],
      };
    });
    return arr;
  };

  const getAllDrivingCategories = () => {
    const arr = Object.keys(DrivingLicenseCategory).map(name => {
      return {
        value: name,
        label:
          DrivingLicenseCategory[name as keyof typeof DrivingLicenseCategory],
      };
    });
    return arr;
  };

  const getSelectedDrivingCategories = () => {
    return getAllDrivingCategories().filter(item =>
      judgment.drivingLicense?.split(',')?.includes(item.value),
    );
  };

  const viewDocument = () => {
    if (!judgment.id) {
      return;
    }
    spd.pdfPrinter.loadAndPrintJudgment(judgment.id).catch(async response => {
      const error = await responseErrors(response as ResponseError);
      setMessage(`Wystąpił problem z wydrukiem orzeczenia. ${error}`);
    });
  };

  const viewDocumentCopy = () => {
    if (!judgment.id) {
      return;
    }
    spd.pdfPrinter
      .loadAndPrintJudgmentWithStampAndSignature(judgment.id)
      .catch(async response => {
        const error = await responseErrors(response as ResponseError);
        setMessage(`Wystąpił problem z wydrukiem orzeczenia. ${error}`);
      });
  };

  /** `statusP1`: omit to keep the current one, null to clear it. */
  const setBadge = (
    signed: boolean | null,
    statusP1?: GetJudgmentWithSignedInfoDtoStatusP1Enum | null,
  ) => {
    setJudgment(prev => ({
      ...prev,
      signed: signed === null ? prev.signed : signed,
      statusP1: statusP1 === undefined ? prev.statusP1 : statusP1,
    }));
  };

  return (
    <Accordion.Item eventKey={props.idx} className="flex-fill">
      <Accordion.Header onClick={props.handleOnClick}>
        <strong>
          {judgment.id
            ? `${judgment.number || '---'}, ${
                judgment?.article?.description ||
                OccupationalMedicineCategory[
                  judgment?.occupationalMedicineOneCategory as keyof typeof OccupationalMedicineCategory
                ] ||
                ''
              }`
            : 'WYPEŁNIJ DANE W ORZECZENIU I ZAPISZ!'}
        </strong>
        {judgment.signed ? (
          <Badge bg="warning" className="ms-2">
            Podpisano elektronicznie
          </Badge>
        ) : (!judgment.withoutSign &&
            judgment.id &&
            judgment.statusP1 === null) ||
          judgment.statusP1 === SimP1StatusEnum.ToSign ? (
          <Badge bg="info" className="ms-2">
            Oczekuje na podpis elektroniczny
          </Badge>
        ) : (
          <></>
        )}
        {judgment.statusP1 === SimP1StatusEnum.AcceptedInP1 &&
        savedWithoutSign ? (
          <Badge bg="secondary" className="ms-2 opacity-75">
            {SimP1StatusEnumText[judgment.statusP1]} – wersja nieaktualna
          </Badge>
        ) : (
          judgment.statusP1 &&
          P1_STATUS_BG[judgment.statusP1] && (
            <Badge bg={P1_STATUS_BG[judgment.statusP1]!} className="ms-2">
              {SimP1StatusEnumText[judgment.statusP1]}
            </Badge>
          )
        )}
      </Accordion.Header>
      <Accordion.Body>
        <Form className="d-grid gap-3">
          <FormRow controlId="drivingCategories" label="Powiązana karta">
            <div className="selector-with-button">
              <div className="selector-input">
                <CardSelector
                  value={judgment?.card?.id}
                  examId={props.exam.id}
                  cards={props.cards}
                  cardType={judgment?.article?.cardType as unknown as CardType}
                  className="test-multi-select"
                  onChange={options =>
                    setJudgment({
                      ...judgment,
                      card: props.cards?.find(a => a.id === options?.value),
                    })
                  }
                  clearable={true}
                />
              </div>
              {judgment?.card?.id && (
                <CardsLink
                  examId={props.exam.id}
                  type={judgment.card.type as unknown as CardType}
                  newWindow
                />
              )}
            </div>
          </FormRow>
          <FormRow controlId="staff" label="Lekarz">
            <ChooseSigned
              userUID={judgment?.psychologist?.uid}
              handleOnChange={user =>
                setJudgment({
                  ...judgment,
                  psychologist: user,
                })
              }
              roles={props.roles}
              isDisabled={judgment?.number ? true : false}
            />
          </FormRow>
          <FormRow controlId="article" label="Podstawa badania">
            <Select
              options={getAllArticles(
                judgment?.card?.type as unknown as CardType,
              )}
              placeholder="Wskaż..."
              defaultValue={
                judgment?.article?.id
                  ? {
                      value: judgment.article.id,
                      label: judgment.article.description,
                    }
                  : undefined
              }
              isDisabled={judgment?.number ? true : false}
              onChange={options => {
                const art = props.articles.find(a => a.id === options?.value);
                setJudgment({
                  ...judgment,
                  article: art,
                  dateTo: undefined,
                  dateFrom: undefined,
                });
                setMessage(art?.attention || '');
                recalculateNextExamDate(
                  art?.id,
                  judgment.judgmentDate,
                  judgment.contactWithFood ?? false,
                );
              }}
            />
          </FormRow>
          {props.mode === ArticleMode.Psychologist && (
            <>
              <FormRow controlId="occupMed" label="Medycyna pracy">
                <Select
                  options={getAllMedOccup()}
                  defaultValue={{
                    value: judgment?.occupationalMedicineOneCategory,
                    label:
                      OccupationalMedicineCategory[
                        judgment?.occupationalMedicineOneCategory as keyof typeof OccupationalMedicineCategory
                      ],
                  }}
                  onChange={options =>
                    setJudgment({
                      ...judgment,
                      occupationalMedicineOneCategory: Object.keys(
                        OccupationalMedicineCategory,
                      ).find(a => a === options?.value),
                    })
                  }
                />
              </FormRow>
              <FormRow controlId="occupMedText" label="Medycyna pracy - inne">
                <Form.Control
                  type="text"
                  defaultValue={judgment?.occupationalMedicineText || ''}
                  onChange={e =>
                    setJudgment({
                      ...judgment,
                      occupationalMedicineText: e.target.value,
                    })
                  }
                />
              </FormRow>
            </>
          )}
          <FormRow controlId="number" label="Numer">
            <Form.Control
              type="text"
              disabled={true}
              value={
                judgment?.id
                  ? judgment?.number || ' ---- '
                  : '-- Numer zostanie nadany automatycznie po zapisie --'
              }
              onChange={e =>
                setJudgment({
                  ...judgment,
                  number: e.target.value,
                })
              }
            />
          </FormRow>
          <FormRow controlId="dateJudgment" label="Data orzeczenia">
            <Form.Control
              type="date"
              name="datepic"
              placeholder="DateRange"
              value={momencik(judgment.judgmentDate, 'YYYY-MM-DD')}
              onChange={e => {
                const newDate = e.target.value
                  ? new Date(e.target.value)
                  : undefined;
                setJudgment({ ...judgment, judgmentDate: newDate });
                recalculateNextExamDate(
                  judgment.article?.id,
                  newDate,
                  judgment.contactWithFood ?? false,
                );
              }}
            />
          </FormRow>
          <FormRow controlId="pass" label="Wynik pozytywny">
            <Form.Check
              type="checkbox"
              defaultChecked={
                judgment?.pass ||
                judgment?.pass === undefined ||
                judgment?.pass === null
                  ? true
                  : false
              }
              onChange={e =>
                setJudgment({
                  ...judgment,
                  pass: e.target.checked,
                })
              }
            />
          </FormRow>
          <FormRow
            controlId="drivingCategories"
            label="Dotyczy kategorii prawa jazdy"
          >
            <Select
              options={getAllDrivingCategories()}
              value={getSelectedDrivingCategories()}
              isMulti
              className="test-multi-select"
              classNamePrefix="driving-select"
              onChange={options =>
                setJudgment({
                  ...judgment,
                  drivingLicense: options.map(o => o.value).join(','),
                })
              }
              placeholder="Wskaż..."
            />
          </FormRow>
          {judgment.article?.pdfTemplateType ===
            GetPdfTemplateDtoTypeEnum.orzeczenie_sanitarno_epidemiologiczne && (
            <FormRow controlId="contactWithFood" label="Kontakt z żywnością">
              <Form.Check
                type="checkbox"
                defaultChecked={judgment?.contactWithFood ? true : false}
                onChange={e => {
                  const checked = e.target.checked;
                  setJudgment({ ...judgment, contactWithFood: checked });
                  recalculateNextExamDate(
                    judgment.article?.id,
                    judgment.judgmentDate,
                    checked,
                  );
                }}
              />
            </FormRow>
          )}
          <FormRow controlId="nextExamDate" label="Data kolejnego badania">
            <Form.Control
              type="date"
              required={false}
              name="datepic"
              placeholder="DateRange"
              value={momencik(judgment?.nextExamDate, 'YYYY-MM-DD')}
              onChange={e => {
                setJudgment({
                  ...judgment,
                  nextExamDate: e.target.value
                    ? new Date(e.target.value)
                    : null,
                });
              }}
            />
          </FormRow>
          {judgment.article?.pdfTemplateType ===
            GetPdfTemplateDtoTypeEnum.wynik_widzenia_zmierzchowego && (
            <>
              <FormRow controlId="twilightVision" label="Widzenie zmierzchowe">
                <Form.Check
                  type="checkbox"
                  defaultChecked={
                    judgment?.twilightVision ||
                    judgment?.twilightVision === undefined ||
                    judgment?.twilightVision === null
                      ? true
                      : false
                  }
                  onChange={e =>
                    setJudgment({
                      ...judgment,
                      twilightVision: e.target.checked,
                    })
                  }
                />
              </FormRow>
              <FormRow
                controlId="glareSensitivity"
                label="Wrażliwość na olśnienie"
              >
                <Form.Check
                  type="checkbox"
                  defaultChecked={
                    judgment?.glareSensitivity ||
                    judgment?.glareSensitivity === undefined ||
                    judgment?.glareSensitivity === null
                      ? true
                      : false
                  }
                  onChange={e =>
                    setJudgment({
                      ...judgment,
                      glareSensitivity: e.target.checked,
                    })
                  }
                />
              </FormRow>
              <FormRow controlId="comment" label="Uwagi">
                <FormControlTextarea3
                  defaultValue={judgment?.comment || ''}
                  onChange={e =>
                    setJudgment({
                      ...judgment,
                      comment: e.target.value,
                    })
                  }
                />
              </FormRow>
            </>
          )}
          {props.mode === ArticleMode.Doctor && (
            <>
              <FormRow
                controlId="vehicleRestriction"
                label="Ograniczenie w zakresie pojazdów"
              >
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={judgment?.vehicleRestriction || ''}
                  onChange={e =>
                    setJudgment({
                      ...judgment,
                      vehicleRestriction: e.target.value,
                    })
                  }
                />
              </FormRow>
              <FormRow
                controlId="personRestriction"
                label="Ograniczenie wobec osoby"
              >
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={judgment?.personRestriction || ''}
                  onChange={e =>
                    setJudgment({
                      ...judgment,
                      personRestriction: e.target.value,
                    })
                  }
                />
              </FormRow>
              <FormRow controlId="roadTransport" label="Transport drogowy">
                <Form.Control
                  as="textarea"
                  rows={1}
                  value={judgment?.roadTransport || ''}
                  onChange={e =>
                    setJudgment({
                      ...judgment,
                      roadTransport: e.target.value,
                    })
                  }
                />
              </FormRow>
            </>
          )}
          {judgment.article?.pdfTemplateType ===
            GetPdfTemplateDtoTypeEnum.mp_orzeczenie_lekarskie_43_transport && (
            <>
              <FormRow controlId="art39j" label="Dopisek o art. 39j na wydruku">
                <Form.Check
                  type="checkbox"
                  checked={judgment?.art39j ? true : false}
                  onChange={e =>
                    setJudgment({
                      ...judgment,
                      art39j: e.target.checked,
                    })
                  }
                />
              </FormRow>
            </>
          )}
          {(judgment.article?.pdfTemplateType ===
            GetPdfTemplateDtoTypeEnum.mp_orzeczenie_lekarskie_43 ||
            judgment.article?.pdfTemplateType ===
              GetPdfTemplateDtoTypeEnum.mp_orzeczenie_lekarskie_43_transport) && (
            <>
              <FormRow
                controlId="requiresCorrectiveEyewear"
                label="Wymagane okulary korekcyjne"
              >
                <Form.Check
                  type="checkbox"
                  checked={judgment?.requiresCorrectiveEyewear ? true : false}
                  onChange={e =>
                    setJudgment({
                      ...judgment,
                      requiresCorrectiveEyewear: e.target.checked,
                    })
                  }
                />
              </FormRow>
            </>
          )}
          {judgment.article?.pdfTemplateType ===
            GetPdfTemplateDtoTypeEnum.nauczyciel_poratowanie_zdrowia && (
            <>
              <FormRow controlId="dateFrom" label="Urlop od">
                <Form.Control
                  type="date"
                  required={false}
                  name="datepic"
                  placeholder="DateRange"
                  value={momencik(judgment?.dateFrom, 'YYYY-MM-DD')}
                  onChange={e => {
                    setJudgment({
                      ...judgment,
                      dateFrom: e.target.value
                        ? new Date(e.target.value)
                        : null,
                    });
                  }}
                />
              </FormRow>
              <FormRow controlId="dateTo" label="Urlop do">
                <Form.Control
                  type="date"
                  required={false}
                  name="datepic"
                  placeholder="DateRange"
                  value={momencik(judgment?.dateTo, 'YYYY-MM-DD')}
                  onChange={e => {
                    setJudgment({
                      ...judgment,
                      dateTo: e.target.value ? new Date(e.target.value) : null,
                    });
                  }}
                />
              </FormRow>
            </>
          )}
          <FormRow controlId="withoutSign" label="Bez podpisu elektronicznego">
            <Form.Check
              type="checkbox"
              checked={judgment?.withoutSign ? true : false}
              onChange={e =>
                setJudgment({
                  ...judgment,
                  withoutSign: e.target.checked,
                })
              }
            />
          </FormRow>
          <FormGroup className="mb-5">
            {judgment?.id && (
              <LastEditorInfo
                className="last-editor-4vh"
                id={judgment?.id}
                method={spd.judgments.getLastEditor}
              />
            )}
          </FormGroup>
          {message && <Alert variant={'info'}>{message}</Alert>}
          <SaveAndPrintJudgment
            judgment={judgment}
            exam={props.exam}
            onSave={saved => {
              setJudgment(saved);
              setSavedWithoutSign(!!saved.withoutSign);
            }}
            setSavingInProgress={setSavingInProgress}
            setBadge={setBadge}
          />
          {judgment?.id && !savingInProgress && (
            <Button
              variant="outline-primary"
              className="ms-1"
              onClick={() => viewDocument()}
            >
              Pokaż wydruk
            </Button>
          )}
          {judgment?.withoutSign &&
            judgment?.id &&
            !judgment?.signed &&
            !savingInProgress && (
              <Button
                variant="outline-primary"
                className="ms-1"
                onClick={() => viewDocumentCopy()}
              >
                Pokaż kopię orzeczenia podpisanego poza systemem
              </Button>
            )}
        </Form>
      </Accordion.Body>
    </Accordion.Item>
  );
};
